import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle, InfoCircle, WarningTriangle, Xmark } from "iconoir-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IconButton } from "@/components/ui/icon-button";
import { Icon } from "@/design-system/icons/icon";
import { m } from "@/i18n/en";

type ToastTone = "success" | "danger" | "info";
type ToastItem = {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
};
type ToastContextValue = {
  showToast: (toast: Omit<ToastItem, "id">) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const showToast = useCallback((toast: Omit<ToastItem, "id">) => {
    setItems((current) => [
      ...current,
      { ...toast, id: Date.now() + current.length },
    ]);
  }, []);
  const value = useMemo(() => ({ showToast }), [showToast]);
  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider duration={5000} swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            className="grid w-[min(24rem,calc(100vw-2rem))] grid-cols-[auto_1fr_auto] items-start gap-3 rounded-[var(--radius-lg)] border border-line bg-elevated p-4 shadow-xl"
            key={item.id}
            onOpenChange={(open) => {
              if (!open)
                setItems((current) =>
                  current.filter((entry) => entry.id !== item.id),
                );
            }}
          >
            <span
              className={
                item.tone === "danger"
                  ? "text-danger"
                  : item.tone === "success"
                    ? "text-[var(--color-status-success)]"
                    : "text-primary"
              }
            >
              <Icon
                glyph={
                  item.tone === "danger"
                    ? WarningTriangle
                    : item.tone === "success"
                      ? CheckCircle
                      : InfoCircle
                }
              />
            </span>
            <div className="min-w-0">
              <ToastPrimitive.Title className="font-semibold">
                {item.title}
              </ToastPrimitive.Title>
              {item.description && (
                <ToastPrimitive.Description className="mt-1 text-sm text-secondary">
                  {item.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close asChild>
              <IconButton className="-m-2 size-10" label={m.close}>
                <Icon glyph={Xmark} />
              </IconButton>
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[80] grid gap-3 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}
